import {
  getDateKeysInRange,
  getMonthKeysInRange,
} from './dashboardDetailsPeriod';

type Bucket = {
  chave?: string;
  date_key?: string;
  day?: number;
  hour?: number;
  month?: number;
  month_key?: string;
  total: number;
};

export interface DashboardDetailsSeries {
  salesOfDay: Bucket[];
  salesOfMonth: Bucket[];
  salesOfYear: Bucket[];
}

export interface DashboardDetailsCoupons {
  couponsOfDay: Bucket[];
  couponsOfMonth: Bucket[];
  couponsOfYear: Bucket[];
}

export type DashboardDetailsGranularity = 'hour' | 'day' | 'month';

interface ChartData {
  labels: string[];
  datasets: { label: string; data: number[] }[];
}

function getBucketTotal(
  rows: Bucket[],
  keyProperty: 'date_key' | 'month_key',
  key: string,
  legacyProperty: 'day' | 'month',
  legacyValue: number,
  allowLegacyKey: boolean,
): number {
  const exact = rows.find((row) => row[keyProperty] === key);

  if (exact) {
    return Number(exact.total);
  }

  if (!allowLegacyKey || rows.some((row) => row[keyProperty])) {
    return 0;
  }

  const legacy = rows.find((row) => Number(row[legacyProperty]) === legacyValue);
  return legacy ? Number(legacy.total) : 0;
}

function getHourlyTotal(rows: Bucket[], hour: number): number {
  const bucket = rows.find((row) => Number(row.hour) === hour);
  return bucket ? Number(bucket.total) : 0;
}

function getDayLabel(dateKey: string): string {
  return `${dateKey.slice(8, 10)}/${dateKey.slice(5, 7)}`;
}

function getMonthLabel(monthKey: string): string {
  return `${monthKey.slice(5, 7)}/${monthKey.slice(0, 4)}`;
}

export function buildSalesChartData(
  atac: DashboardDetailsSeries,
  ifood: DashboardDetailsSeries,
  pdv: DashboardDetailsSeries,
  granularity: DashboardDetailsGranularity,
  startDateKey: string,
  endDateKey: string,
): ChartData {
  let labels: string[];
  let atacRows: Bucket[];
  let ifoodRows: Bucket[];
  let pdvRows: Bucket[];
  let keyProperty: 'date_key' | 'month_key';
  let legacyProperty: 'day' | 'month';
  let keys: string[];
  let legacyAllowed: boolean;

  if (granularity === 'hour') {
    labels = Array.from({ length: 24 }, (_, hour) =>
      `${String(hour).padStart(2, '0')}h`,
    );

    return {
      labels,
      datasets: [
        { label: 'Atac', data: labels.map((_, hour) => getHourlyTotal(atac.salesOfDay, hour)) },
        { label: 'iFood', data: labels.map((_, hour) => getHourlyTotal(ifood.salesOfDay, hour)) },
        { label: 'Atac PDV', data: labels.map((_, hour) => getHourlyTotal(pdv.salesOfDay, hour)) },
      ],
    };
  }

  if (granularity === 'day') {
    keys = getDateKeysInRange(startDateKey, endDateKey);
    labels = keys.map(getDayLabel);
    atacRows = atac.salesOfMonth;
    ifoodRows = ifood.salesOfMonth;
    pdvRows = pdv.salesOfMonth;
    keyProperty = 'date_key';
    legacyProperty = 'day';
    legacyAllowed = startDateKey.slice(0, 7) === endDateKey.slice(0, 7);
  } else {
    keys = getMonthKeysInRange(startDateKey, endDateKey);
    labels = keys.map(getMonthLabel);
    atacRows = atac.salesOfYear;
    ifoodRows = ifood.salesOfYear;
    pdvRows = pdv.salesOfYear;
    keyProperty = 'month_key';
    legacyProperty = 'month';
    legacyAllowed = startDateKey.slice(0, 4) === endDateKey.slice(0, 4);
  }

  const mapRows = (rows: Bucket[]): number[] =>
    keys.map((key) =>
      getBucketTotal(
        rows,
        keyProperty,
        key,
        legacyProperty,
        Number(keyProperty === 'date_key' ? key.slice(8, 10) : key.slice(5, 7)),
        legacyAllowed,
      ),
    );

  return {
    labels,
    datasets: [
      { label: 'Atac', data: mapRows(atacRows) },
      { label: 'iFood', data: mapRows(ifoodRows) },
      { label: 'Atac PDV', data: mapRows(pdvRows) },
    ],
  };
}

export function buildCouponsChartData(
  coupons: DashboardDetailsCoupons,
  granularity: DashboardDetailsGranularity,
  startDateKey: string,
  endDateKey: string,
): ChartData {
  let labels: string[];
  let keys: string[];
  let rows: Bucket[];
  let keyProperty: 'date_key' | 'month_key';
  let legacyProperty: 'day' | 'month';
  let legacyAllowed: boolean;

  if (granularity === 'hour') {
    rows = coupons.couponsOfDay;
    keys = Array.from({ length: 24 }, (_, hour) => String(hour));
    labels = keys.map((hour) => `${String(Number(hour)).padStart(2, '0')}h`);

    return {
      labels,
      datasets: Array.from(new Set(rows.map((row) => row.chave).filter(Boolean))).map(
        (couponKey) => ({
          label: couponKey as string,
          data: keys.map((hour) => {
            const bucket = rows.find(
              (row) => row.chave === couponKey && Number(row.hour) === Number(hour),
            );
            return bucket ? Number(bucket.total) : 0;
          }),
        }),
      ),
    };
  }

  if (granularity === 'day') {
    rows = coupons.couponsOfMonth;
    keys = getDateKeysInRange(startDateKey, endDateKey);
    labels = keys.map(getDayLabel);
    keyProperty = 'date_key';
    legacyProperty = 'day';
    legacyAllowed = startDateKey.slice(0, 7) === endDateKey.slice(0, 7);
  } else {
    rows = coupons.couponsOfYear;
    keys = getMonthKeysInRange(startDateKey, endDateKey);
    labels = keys.map(getMonthLabel);
    keyProperty = 'month_key';
    legacyProperty = 'month';
    legacyAllowed = startDateKey.slice(0, 4) === endDateKey.slice(0, 4);
  }

  return {
    labels,
    datasets: Array.from(new Set(rows.map((row) => row.chave).filter(Boolean))).map(
      (couponKey) => ({
        label: couponKey as string,
        data: keys.map((key) =>
          getBucketTotal(
            rows.filter((row) => row.chave === couponKey),
            keyProperty,
            key,
            legacyProperty,
            Number(keyProperty === 'date_key' ? key.slice(8, 10) : key.slice(5, 7)),
            legacyAllowed,
          ),
        ),
      }),
    ),
  };
}

export function hasAmbiguousLegacySalesBuckets(
  series: DashboardDetailsSeries,
  granularity: DashboardDetailsGranularity,
  startDateKey: string,
  endDateKey: string,
): boolean {
  if (granularity === 'day' && startDateKey.slice(0, 7) !== endDateKey.slice(0, 7)) {
    return series.salesOfMonth.some((row) => !row.date_key);
  }

  if (
    granularity === 'month' &&
    startDateKey.slice(0, 4) !== endDateKey.slice(0, 4)
  ) {
    return series.salesOfYear.some((row) => !row.month_key);
  }

  return false;
}
